import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { X, CheckCircle, Ticket } from "lucide-react";
import { API_URL } from "@/config";
import { useTickets } from "@/context/TicketContext";
import { useSubscribers } from "@/context/SubscriberContext";
import { Header } from "@/components/Header";
import { DeviceCard } from "@/components/DeviceCard";
import { TicketFormModal } from "@/components/TicketFormModal";

const DeviceDetailPage = () => {
  const { subscriberId } = useParams();
  const navigate = useNavigate();
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const { addTicket, getTicketedDeviceIds } = useTickets();
  const { subscribers } = useSubscribers();
  const [allDevices, setAllDevices] = useState([]);
  
  const subscriber = subscribers.find(s => s.id === parseInt(subscriberId));
  
  // Fetch devices from API
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscribers/${subscriberId}/devices`);
        const data = await res.json();
        setAllDevices(data);
      } catch (err) {
        console.error('Failed to fetch devices:', err);
      }
    };
    fetchDevices();
  }, [subscriberId]);

  // Filter out devices that already have tickets
  const ticketedDeviceIds = getTicketedDeviceIds();
  const devices = allDevices.filter(d => !ticketedDeviceIds.includes(d.id));

  const handleCreateTicket = (device) => {
    setSelectedDevice(device);
    setShowTicketModal(true);
  };

  const handleCloseModal = () => {
    setShowTicketModal(false);
    setSelectedDevice(null);
  };

  const handleSubmitTicket = (formData) => {
    addTicket({
      ...formData,
      subscriberId: subscriber.id,
      subscriberName: subscriber.name,
    });
    handleCloseModal();
  };
  
  if (!subscriber) {
    return (
      <div className="app-container">
        <div className="device-page-header">
          <h1>Subscriber not found</h1>
          <button className="close-btn" onClick={() => navigate('/')}>
            <X size={24} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="device-detail-page">
      <Header />

      {/* Device Page Content */}
      <main className="main-content">
        <div className="device-page-header">
          <div>
            <h1 className="device-page-title">{subscriber.name} - Need Attention</h1>
            <p className="device-page-subtitle">Showing {devices.length} of {allDevices.length} devices</p>
          </div>
          <button className="close-btn" onClick={() => navigate('/')} data-testid="close-btn">
            <X size={24} />
          </button>
        </div>

        {devices.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <CheckCircle size={48} className="empty-state-icon" />
            <h3>All Devices Have Tickets</h3>
            <p>All devices for this subscriber have been assigned tickets.</p>
            <button className="btn-back" onClick={() => navigate('/')}>
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div className="devices-list" data-testid="devices-list">
            {devices.map(device => (
              <DeviceCard 
                key={device.id} 
                device={device} 
                subscriber={subscriber}
                onCreateTicket={handleCreateTicket}
              />
            ))}
          </div>
        )}
      </main>

      {/* Ticket Form Modal */}
      {showTicketModal && selectedDevice && (
        <TicketFormModal 
          device={selectedDevice}
          subscriber={subscriber}
          onClose={handleCloseModal}
          onSubmit={handleSubmitTicket}
        />
      )}
    </div>
  );
};

export default DeviceDetailPage;
